import { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import AppHeader from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/lib/toast";
import { ShieldCheck, Trash2, RefreshCw, Search, ChevronLeft, Download, Calendar, ChevronDown } from "lucide-react";
import { cn, matchesSearch } from "@/lib/utils";

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  CREATE_PATIENT: { label: "New Patient", color: "bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300" },
  CONFIRM_TAGS: { label: "Tags Confirmed", color: "bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300" },
  DELETE_PATIENT: { label: "Patient Deleted", color: "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300" },
  LOGIN: { label: "Login", color: "bg-gray-100 text-gray-700 dark:bg-muted dark:text-muted-foreground" },
  LOGOUT: { label: "Logout", color: "bg-gray-100 text-gray-700 dark:bg-muted dark:text-muted-foreground" },
  UPLOAD_FILES: { label: "Files Uploaded", color: "bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300" },
};

/** A Drive patient entry as returned by listDrivePatients */
interface DrivePatient {
  id: string;   // Drive folder ID
  name: string; // Folder name (display only)
}

export default function AuditLog() {
  const { user, loading } = useAuth({ redirectOnUnauthenticated: true });
  const [, navigate] = useLocation();

  const [folderFilter, setFolderFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // deleteTarget holds the Drive folder ID + display name
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Delete Patient autocomplete
  const [deleteQuery, setDeleteQuery] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const deleteRef = useRef<HTMLDivElement>(null);

  const applyPreset = (preset: "today" | "7d" | "30d" | "month") => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const todayStr = fmt(now);
    if (preset === "today") {
      setStartDate(todayStr); setEndDate(todayStr);
    } else if (preset === "7d") {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      setStartDate(fmt(d)); setEndDate(todayStr);
    } else if (preset === "30d") {
      const d = new Date(now); d.setDate(d.getDate() - 29);
      setStartDate(fmt(d)); setEndDate(todayStr);
    } else if (preset === "month") {
      setStartDate(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`);
      setEndDate(todayStr);
    }
  };

  // Redirect non-admins
  if (!loading && user && user.role !== "admin") {
    navigate("/");
    return null;
  }

  const { data: entries = [], isLoading, refetch } = trpc.admin.getAuditLog.useQuery(
    {
      driveFolderId: folderFilter || undefined,
      action: actionFilter !== "all" ? actionFilter : undefined,
      limit: 300,
      offset: 0,
    },
    { enabled: !!user && user.role === "admin" }
  );

  // CSV export
  const exportCsv = () => {
    const headers = ["Timestamp", "User", "Action", "Drive Folder ID", "Detail", "IP Address"];
    const rows = filtered.map((e) => [
      new Date(e.createdAt).toLocaleString(),
      e.userName ?? e.userId ?? "",
      ACTION_LABELS[e.action]?.label ?? e.action,
      e.driveFolderId ?? "",
      e.detail ? JSON.stringify(e.detail) : "",
      e.ipAddress ?? "",
    ]);
    const escape = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `audit-log-${date}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 200);
  };

  // Load Drive patients for the autocomplete pickers
  const { data: drivePatientsRaw = [] } = trpc.gdrive.listDrivePatients.useQuery(
    undefined,
    { enabled: !!user && user.role === "admin" }
  );
  // Normalise to { id, name } — DrivePatient uses folderId not id
  const drivePatients: DrivePatient[] = useMemo(() =>
    drivePatientsRaw.map((p: any) => ({ id: p.folderId, name: p.name })),
    [drivePatientsRaw]
  );

  const deletePatient = trpc.admin.deletePatient.useMutation({
    onSuccess: () => {
      toast.success(`Patient data for "${deleteTarget?.name}" deleted.`);
      setDeleteTarget(null);
      refetch();
    },
    onError: (err) => {
      toast.error(`Delete failed: ${err.message}`);
      setDeleteTarget(null);
    },
  });

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (deleteRef.current && !deleteRef.current.contains(e.target as Node)) setDeleteOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const deleteMatches = useMemo(() =>
    drivePatients.filter((p) => matchesSearch(p.name, deleteQuery)).slice(0, 8),
    [drivePatients, deleteQuery]
  );

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (folderFilter && !(e.driveFolderId ?? "").toLowerCase().includes(folderFilter.toLowerCase())) return false;
      if (startDate) {
        const entryDate = new Date(e.createdAt);
        const start = new Date(startDate + "T00:00:00");
        if (entryDate < start) return false;
      }
      if (endDate) {
        const entryDate = new Date(e.createdAt);
        const end = new Date(endDate + "T23:59:59");
        if (entryDate > end) return false;
      }
      return true;
    });
  }, [entries, folderFilter, startDate, endDate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1">
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          <ShieldCheck className="w-6 h-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">HIPAA Audit Log</h1>
            <p className="text-sm text-muted-foreground">
              All PHI-touching actions are recorded here. Admin access only.
            </p>
          </div>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={exportCsv}
              disabled={filtered.length === 0}
            >
              <Download className="w-4 h-4" />
              Download CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => refetch()}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Filter by Drive folder ID…"
              value={folderFilter}
              onChange={(e) => setFolderFilter(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.entries(ACTION_LABELS).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date range row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground whitespace-nowrap">From</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground whitespace-nowrap">To</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[160px]"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {(["today", "7d", "30d", "month"] as const).map((p) => (
              <Button
                key={p}
                variant="outline"
                size="sm"
                className="text-xs h-8 px-2"
                onClick={() => applyPreset(p)}
              >
                {p === "today" ? "Today" : p === "7d" ? "Last 7 Days" : p === "30d" ? "Last 30 Days" : "This Month"}
              </Button>
            ))}
            {(startDate || endDate) && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8 px-2 text-muted-foreground"
                onClick={() => { setStartDate(""); setEndDate(""); }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Patient deletion panel */}
        <div className="border border-destructive/30 rounded-lg p-4 mb-6 bg-destructive/5">
          <h2 className="font-semibold text-destructive mb-2 flex items-center gap-2">
            <Trash2 className="w-4 h-4" />
            Delete Patient Data
          </h2>
          <p className="text-sm text-muted-foreground mb-3">
            Permanently removes all upload sessions and footage file records for a patient. The Google Drive folder itself is <strong>not</strong> deleted — remove it manually if required. This action cannot be undone and will be recorded in the audit log.
          </p>
          {drivePatients.length === 0 ? (
            <span className="text-sm text-muted-foreground">No patients found in Google Drive.</span>
          ) : (
            <div ref={deleteRef} className="relative max-w-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  className="w-full pl-9 pr-8 py-2 text-sm border border-destructive/40 rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-destructive/40 text-foreground placeholder:text-muted-foreground"
                  placeholder="Search patient to delete…"
                  value={deleteQuery}
                  onChange={(e) => { setDeleteQuery(e.target.value); setDeleteOpen(true); }}
                  onFocus={() => setDeleteOpen(true)}
                />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
              {deleteOpen && deleteMatches.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg overflow-hidden">
                  {deleteMatches.map((p) => (
                    <button
                      key={p.id}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-destructive/10 text-destructive transition-colors"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setDeleteTarget({ id: p.id, name: p.name });
                        setDeleteQuery("");
                        setDeleteOpen(false);
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 shrink-0" />
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
              {deleteOpen && deleteQuery.length > 0 && deleteMatches.length === 0 && (
                <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg px-3 py-2 text-sm text-muted-foreground">
                  No patients match "{deleteQuery}"
                </div>
              )}
            </div>
          )}
        </div>

        {/* Log table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            No audit log entries found.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Timestamp</th>
                  <th className="text-left px-4 py-3 font-medium">User</th>
                  <th className="text-left px-4 py-3 font-medium">Action</th>
                  <th className="text-left px-4 py-3 font-medium">Drive Folder ID</th>
                  <th className="text-left px-4 py-3 font-medium">Detail</th>
                  <th className="text-left px-4 py-3 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((entry, idx) => {
                  const meta = ACTION_LABELS[entry.action] ?? {
                    label: entry.action,
                    color: "bg-gray-100 text-gray-700 dark:bg-muted dark:text-muted-foreground",
                  };
                  return (
                    <tr
                      key={entry.id}
                      className={idx % 2 === 0 ? "bg-background" : "bg-muted/20"}
                    >
                      <td className="px-4 py-2 whitespace-nowrap text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {entry.userName ?? entry.userId ?? "—"}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{entry.driveFolderId ?? "—"}</td>
                      <td className="px-4 py-2 max-w-xs truncate text-muted-foreground text-xs">
                        {entry.detail ? JSON.stringify(entry.detail) : "—"}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground text-xs">
                        {entry.ipAddress ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3 text-right">
          {filtered.length} entr{filtered.length === 1 ? "y" : "ies"} shown
        </p>
      </main>


      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Delete Patient Data: {deleteTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all upload sessions, footage file records, and access
              assignments for this patient. The Google Drive folder will <strong>not</strong> be
              removed — delete it manually if required. This action cannot be undone and will be
              recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                if (deleteTarget) {
                  deletePatient.mutate({ driveFolderId: deleteTarget.id });
                }
              }}
            >
              {deletePatient.isPending ? "Deleting…" : "Yes, Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
